import { DefaultParamType, TFnType, TranslationKey, useTranslate } from '@tolgee/react';
import { useEffect, useRef, useState } from 'react';
import styled from 'styled-components';

import theme from '../../../assets/theme';
import useResponses from '../../../hooks/useResponses';
import { RoundType } from '../../../services/backendService/types';
import { zIndexes } from '../../../utils/zIndexes';
import { Slide } from '../../SlideComponents/slide.types';

export const toSlideType = (
  t: TFnType<DefaultParamType, string, TranslationKey>,
  type?: string | RoundType,
  plural?: boolean,
) => {
  if (type === 'answers')
    return { count: 'words', description: t(`Word${plural ? 's written' : ' written'}`) };
  if (type === 'aiAssessedAnswers')
    return { count: 'words', description: t(`Word${plural ? 's written' : ' written'}`) };
  if (type === 'aiAssessedImageAnswers')
    return { count: 'details', description: t(`Detail${plural ? 's added' : ' added'}`) };
  if (type === 'wordcloud')
    return { count: 'words', description: t(`Word${plural ? 's added' : ' added'}`) };
  if (type === 'canvas')
    return { count: 'details', description: t(`Detail${plural ? 's added' : ' added'}`) };
  if (type === 'poll')
    return { count: 'responses', description: t(`Vote${plural ? 's' : ''}`) };
  if (type === 'voting')
    return { count: 'votes', description: `Vote${plural ? 's' : ''}` };
  return { count: '', description: '' };
};

const ActivitySpecificResponseSummary = ({
  currentSlide,
  gameId,
  tenantId,
}: {
  currentSlide: Slide;
  gameId: string;
  tenantId: string;
}) => {
  const { wordCount, count, detailCount } = useResponses(
    gameId,
    currentSlide.id,
    true,
    tenantId,
  );
  const { t } = useTranslate();

  // Determine which count to display
  const slideTypeInfo = toSlideType(t, currentSlide.type, true);
  const targetCount =
    slideTypeInfo.count === 'words'
      ? wordCount
      : slideTypeInfo.count === 'details'
      ? detailCount
      : count;

  // Animation logic
  const [displayCount, setDisplayCount] = useState(0);
  const previousCountRef = useRef(0);

  useEffect(() => {
    if (targetCount <= previousCountRef.current) {
      setDisplayCount(targetCount);
      previousCountRef.current = targetCount;
      return;
    }

    const startValue = previousCountRef.current;
    const endValue = targetCount;
    const duration = 3500; // 2.83 seconds
    const startTime = performance.now();

    const animate = (currentTime: number) => {
      const elapsed = currentTime - startTime;
      const progress = Math.min(elapsed / duration, 1);

      // Ease-out cubic
      const easeOut = 1 - Math.pow(1 - progress, 5);

      const currentValue = Math.round(startValue + (endValue - startValue) * easeOut);
      setDisplayCount(currentValue > 0 ? currentValue : 0);

      if (progress < 1) {
        requestAnimationFrame(animate);
      } else {
        previousCountRef.current = endValue;
      }
    };

    requestAnimationFrame(animate);
  }, [targetCount]);

  // Get description with correct pluralization based on displayed count
  const description =
    slideTypeInfo.count === 'words'
      ? toSlideType(t, currentSlide.type, displayCount !== 1).description
      : slideTypeInfo.count === 'details'
      ? toSlideType(t, currentSlide.type, displayCount !== 1).description
      : toSlideType(t, currentSlide.type, displayCount !== 1).description;

  return (
    <FullSlideBlur>
      <ResponseCounterWidgetContainerCenter style={{ height: 'auto' }}>
        <p
          style={{
            width: '100%',
            fontSize: '80px',
            marginBottom: '0px',
            lineHeight: '80px',
          }}
        >
          {displayCount}
        </p>
        <div style={{ fontSize: '30px' }}>{description}</div>
      </ResponseCounterWidgetContainerCenter>
    </FullSlideBlur>
  );
};

const ResponseCounterWidgetContainerCenter = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  color: ${theme.colors.white};
  padding: 20px;
  font-weight: 600;
  box-shadow: 0px 4px 4px rgba(0, 0, 0, 0.25);
  right: 50%;
  border-radius: 10px;
  background-color: ${theme.colors.translucentLightBlack};
  position: absolute;
  font-size: 36px;
  overflow-wrap: break-word;
  word-break: break-word;
  text-align: center;
  top: 50%;
  transform: translateX(50%) translateY(-50%);
  z-index: ${zIndexes('RESPONSECOUNTER_WIDGET_CONTAINER_CENTER')};
  max-width: 500px;
`;

const FullSlideBlur = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  backdrop-filter: blur(10px);
  -webkit-backdrop-filter: blur(10px);
  background-color: ${theme.colors.lessTranslucentWhite};
  z-index: ${zIndexes('RESPONSECOUNTER_WIDGET_CONTAINER_CENTER')};
`;

export default ActivitySpecificResponseSummary;
